import { ComponentProps, ReactNode, useState, JSX } from 'react'

import { Blocks, Check, ChevronsUpDown, PlusSquare, Shield } from 'lucide-react'

import { DisplayImage } from '~/components/DisplayImage'
import { LogoutButton } from '~/components/Layout/LogoutButton'
import { OrgSelectorDialogMenuItem } from '~/components/Layout/OrgSelectorDialogMenuItem'
import { SplitsLink } from '~/components/Link'
import {
  Dialog,
  DialogTrigger,
  DrawerContent,
} from '~/components/component-library/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { Skeleton } from '~/components/component-library/Skeleton'
import { NewOrgModal } from '~/components/settings/NewOrgModal'
import { useActiveOrg, useSetActiveOrg } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useNavigation } from '~/hooks/useNavigation'
import { useIsMobile } from '~/utils/devices'
import { cn } from '~/utils/tailwind-utils'

function OrgSelectorDrawerContent(
  props: Omit<
    ComponentProps<typeof DrawerContent>,
    'closeButton' | 'dialogTitle' | 'hideTitle'
  >,
) {
  return (
    <DrawerContent
      {...props}
      closeButton={{ enabled: true }}
      dialogTitle={props.title}
      hideTitle={false}
      zIndex={60}
    />
  )
}

export function OrgSelectorActionIcon({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex h-8 w-8 flex-shrink-0 items-center justify-center md:h-5 md:w-5',
        className,
      )}
    >
      {children}
    </div>
  )
}

const adminNavItems = [
  {
    path: '/admin/',
    label: 'Admin tools',
    icon: <Shield className="h-5 w-5 text-secondary md:h-3.5 md:w-3.5" />,
  },
  {
    path: '/component-library',
    label: 'Component library',
    icon: <Blocks className="h-5 w-5 text-secondary md:h-3.5 md:w-3.5" />,
  },
]

export function OrgSelector({
  trailing,
}: {
  /** Rendered after the selector, inside the same header row. */
  trailing?: ReactNode
}): JSX.Element {
  const { user: authUser } = useAuthUser()
  const { org: activeOrg } = useActiveOrg()
  const { setActiveOrg } = useSetActiveOrg()

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isNewWorkspaceModalOpen, setIsNewWorkspaceModalOpen] = useState(false)

  const isMobile = useIsMobile()
  const navigate = useNavigation()

  const paddingClassName = cn('p-3 md:p-0')

  const SelectorComponent = isMobile ? Dialog : DropdownMenu
  const TriggerComponent = isMobile ? DialogTrigger : DropdownMenuTrigger
  const ContentComponent = isMobile
    ? OrgSelectorDrawerContent
    : DropdownMenuContent
  const ItemComponent = isMobile ? OrgSelectorDialogMenuItem : DropdownMenuItem

  return (
    <div
      className={cn(
        'sticky top-0 z-40 flex items-center bg-gray-50 md:bg-transparent dark:bg-gray-900 dark:md:bg-transparent',
        paddingClassName,
      )}
    >
      <SelectorComponent open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <TriggerComponent asChild>
          <div
            className={cn(
              'flex w-full cursor-pointer items-center space-x-2 rounded-lg p-1.5',
              {
                'hover:bg-surface-strong': !!activeOrg,
              },
            )}
          >
            {activeOrg ? (
              <DisplayImage
                imageUrl={activeOrg.logoUrl}
                alt={`${activeOrg.name} logo`}
                className="h-7 w-7 rounded-md border border-muted"
              />
            ) : (
              <Skeleton size="sm" className="h-7 w-7 flex-shrink-0" />
            )}
            <div className="leading-0 text-md flex-1 truncate font-medium">
              {activeOrg ? (
                activeOrg.name
              ) : (
                <Skeleton size="sm" className="h-6 w-32 flex-shrink-0" />
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-secondary group-hover:text-primary" />
          </div>
        </TriggerComponent>
        <ContentComponent
          align="start"
          side="bottom"
          sideOffset={4}
          alignOffset={0}
          className="overflow-hidden p-1 text-base focus:outline-none md:w-56 md:max-w-xs md:text-sm"
        >
          <div className="px-3 py-2 text-sm text-secondary md:text-xs">
            Workspaces
          </div>
          {authUser?.orgs.map((org, index) => (
            <ItemComponent
              key={index}
              onClick={() => {
                setActiveOrg(org, '/')
                setIsDropdownOpen(false)
              }}
            >
              <div className="flex flex-grow items-center space-x-2 overflow-hidden">
                <OrgSelectorActionIcon className="relative">
                  <DisplayImage
                    imageUrl={org.logoUrl}
                    alt={`${org.name} logo`}
                  />
                </OrgSelectorActionIcon>
                <div className="w-full truncate">{org.name}</div>
              </div>
              {org.id === activeOrg?.id && (
                <Check className="ml-1 h-6 w-6 md:h-4 md:w-4" />
              )}
            </ItemComponent>
          ))}
          <ItemComponent
            onClick={() => {
              setIsDropdownOpen(false)
              setIsNewWorkspaceModalOpen(true)
            }}
          >
            <div className="flex items-center space-x-2">
              <OrgSelectorActionIcon>
                <PlusSquare className="h-5 w-5 text-blue-500 md:h-3.5 md:w-3.5" />
              </OrgSelectorActionIcon>
              <div>Create a workspace</div>
            </div>
          </ItemComponent>
          <DropdownMenuSeparator />
          {authUser && authUser.isSplitsAdmin && (
            <>
              {adminNavItems.map(({ path, icon, label }) => {
                const content = (
                  <div className="flex items-center space-x-2">
                    <OrgSelectorActionIcon>{icon}</OrgSelectorActionIcon>
                    <div>{label}</div>
                  </div>
                )
                return isMobile ? (
                  <ItemComponent
                    key={path}
                    onClick={() => {
                      setIsDropdownOpen(false)
                      navigate(path)
                    }}
                  >
                    {content}
                  </ItemComponent>
                ) : (
                  <DropdownMenuItem key={path} asChild>
                    <SplitsLink
                      path={path}
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {content}
                    </SplitsLink>
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
            </>
          )}
          <LogoutButton />
        </ContentComponent>
        <Dialog
          open={isNewWorkspaceModalOpen}
          onOpenChange={setIsNewWorkspaceModalOpen}
        >
          <NewOrgModal
            isModalOpen={isNewWorkspaceModalOpen}
            setIsModalOpen={setIsNewWorkspaceModalOpen}
          />
        </Dialog>
      </SelectorComponent>
      {trailing}
    </div>
  )
}
